import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Activity, Users, Eye, Smartphone, Monitor, Bot, TrendingUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Widget Live Traffic — mostra attività istantanea del sito.
 *
 * Polling ogni 5s su /api/admin/traffic/realtime. GDPR-compliant (ip_hash salt giornaliero).
 * - Pulse sparkline ultimi 5 min (10 bucket 30s)
 * - Active now, pageviews 5m, requests, bot
 * - Top path, breakdown mobile/desktop
 * - 24h summary
 */
export default function LiveTrafficWidget() {
  const [live, setLive] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const summaryRef = useRef(null);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/traffic/realtime`, { withCredentials: true });
        setLive(data);
        setError(null);
      } catch (e) {
        setError(e?.response?.data?.detail || "Errore caricamento traffic");
      } finally {
        setLoading(false);
      }
    };
    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/traffic/summary?days=7`, { withCredentials: true });
        setSummary(data);
      } catch (e) {
        // silenzioso, realtime e' prioritario
      }
    };

    fetchLive();
    fetchSummary();
    pollRef.current = setInterval(fetchLive, 5000);
    summaryRef.current = setInterval(fetchSummary, 60000); // 1 min

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (summaryRef.current) clearInterval(summaryRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 p-6 flex items-center gap-3 text-zinc-500" data-testid="traffic-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Caricamento traffic...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-800" data-testid="traffic-widget-error">
        <p className="text-sm font-semibold">Errore traffic counter</p>
        <p className="text-xs mt-1">{error}</p>
      </div>
    );
  }

  const maxPulse = Math.max(...(live?.pulse_30s || [1]), 1);
  const pv24 = summary?.pv_24h || 0;
  const uniq24 = summary?.unique_24h || 0;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="traffic-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#163321] to-[#0f2217] px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Activity className="w-5 h-5 text-[#F4C4A8]" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse ring-2 ring-[#163321]" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Traffic live</h3>
              <p className="text-[11px] text-white/60 leading-tight">aggiornato ogni 5s · GDPR safe</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums" data-testid="traffic-active-now">{live?.active_now ?? 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/60">ora online</p>
          </div>
        </div>
        {/* Sparkline pulse */}
        <div className="flex items-end gap-1 h-10 mt-3">
          {(live?.pulse_30s || []).map((v, i) => (
            <div
              key={i}
              className="flex-1 rounded-t bg-[#F4C4A8]/80 transition-all duration-500"
              style={{ height: `${Math.max(8, (v / maxPulse) * 100)}%` }}
              title={`${v} pageview nei 30s`}
            />
          ))}
        </div>
        <p className="text-[10px] text-white/50 mt-1 text-right">ultimi 5 min</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-100 border-b border-zinc-100">
        <Stat
          icon={<Eye className="w-4 h-4 text-[#163321]" />}
          label="Pageview 5m"
          value={live?.pageviews_5m ?? 0}
          testId="traffic-pv-5m"
        />
        <Stat
          icon={<Users className="w-4 h-4 text-[#D97B58]" />}
          label="Unici 24h"
          value={uniq24}
          testId="traffic-uniq-24h"
        />
        <Stat
          icon={<TrendingUp className="w-4 h-4 text-emerald-600" />}
          label="Pageview 24h"
          value={pv24}
          testId="traffic-pv-24h"
        />
        <Stat
          icon={<Bot className="w-4 h-4 text-zinc-400" />}
          label="Bot 5m"
          value={live?.bot_requests_5m ?? 0}
          testId="traffic-bot-5m"
          dim
        />
      </div>

      {/* Device breakdown + top path */}
      <div className="grid md:grid-cols-2 gap-0 md:divide-x divide-zinc-100">
        <div className="p-5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Dispositivi (ultimi 5 min)</p>
          <DeviceBar
            mobile={live?.devices?.mobile ?? 0}
            desktop={live?.devices?.desktop ?? 0}
          />
        </div>
        <div className="p-5 border-t md:border-t-0 border-zinc-100">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Pagine più viste ora</p>
          <ul className="space-y-1.5 text-sm" data-testid="traffic-top-paths">
            {(live?.top_paths || []).slice(0, 5).map((t) => (
              <li key={t.path} className="flex items-center justify-between gap-2">
                <span className="truncate text-zinc-700 font-mono text-xs" title={t.path}>{t.path}</span>
                <span className="tabular-nums text-zinc-500 text-xs bg-zinc-100 px-2 py-0.5 rounded">{t.count}</span>
              </li>
            ))}
            {(!live?.top_paths || live.top_paths.length === 0) && (
              <li className="text-xs text-zinc-400 italic">Nessun traffico negli ultimi 5 min</li>
            )}
          </ul>
        </div>
      </div>

      {/* Referrer top */}
      {summary?.referrers_24h?.length > 0 && (
        <div className="px-5 pb-5 border-t border-zinc-100 pt-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Top referrer 24h</p>
          <ul className="space-y-1 text-xs" data-testid="traffic-referrers">
            {summary.referrers_24h.slice(0, 4).map((r) => (
              <li key={r.referer} className="flex items-center justify-between gap-2">
                <span className="truncate text-zinc-600" title={r.referer}>{truncateUrl(r.referer)}</span>
                <span className="tabular-nums text-zinc-400">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, testId, dim = false }) {
  return (
    <div className={`p-4 ${dim ? "opacity-70" : ""}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      </div>
      <p className="text-xl md:text-2xl font-bold text-zinc-900 tabular-nums" data-testid={testId}>
        {value.toLocaleString("it-IT")}
      </p>
    </div>
  );
}

function DeviceBar({ mobile, desktop }) {
  const total = mobile + desktop;
  const mobilePct = total ? (mobile / total) * 100 : 0;
  return (
    <div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden flex" data-testid="traffic-device-bar">
        {total > 0 && (
          <>
            <div className="bg-[#163321] h-full transition-all" style={{ width: `${mobilePct}%` }} />
            <div className="bg-[#D97B58] h-full transition-all" style={{ width: `${100 - mobilePct}%` }} />
          </>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 text-xs">
        <div className="flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5 text-[#163321]" />
          <span className="text-zinc-600">Mobile</span>
          <span className="text-zinc-400 tabular-nums">{mobile}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Monitor className="w-3.5 h-3.5 text-[#D97B58]" />
          <span className="text-zinc-600">Desktop</span>
          <span className="text-zinc-400 tabular-nums">{desktop}</span>
        </div>
      </div>
    </div>
  );
}

function truncateUrl(url) {
  try {
    const u = new URL(url);
    return `${u.hostname}${u.pathname.length > 1 ? u.pathname.slice(0, 20) : ""}`;
  } catch {
    return url.slice(0, 40);
  }
}
